import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Cpu, CheckCircle2, AlertTriangle, Play, HelpCircle, ArrowRight, KeyRound, RefreshCw, Trash2, ShieldCheck } from "lucide-react";
import type { AIConfig as IAIConfig } from "../types";
import { safeResponseJson } from "../utils/api";

type AIProvider = "gemini" | "openrouter";

interface AIConfigProps {
  aiConfig?: IAIConfig;
  onUpdateAI: (updated: IAIConfig) => void;
  onCredentialStatusChange?: (provider: AIProvider, configured: boolean) => void;
  geminiActive: boolean;
  openrouterActive: boolean;
  readOnly?: boolean;
}

interface TestErrorState {
  message?: string;
  messageKey?: string;
}

export default function AIConfig({
  aiConfig = { provider: "gemini", model: "gemini-3.5-flash" },
  onUpdateAI,
  onCredentialStatusChange,
  geminiActive,
  openrouterActive,
  readOnly = false
}: AIConfigProps) {
  const { t, i18n } = useTranslation("ai");
  const [customModel, setCustomModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [isSavingCredential, setIsSavingCredential] = useState(false);
  const [credentialError, setCredentialError] = useState("");
  const [credentialSaved, setCredentialSaved] = useState(false);
  const [isEditingCredential, setIsEditingCredential] = useState(false);
  const [isTestingCredential, setIsTestingCredential] = useState(false);
  const [isRemovingCredential, setIsRemovingCredential] = useState(false);
  const [credentialTestMessage, setCredentialTestMessage] = useState("");
  const [credentialTestSucceeded, setCredentialTestSucceeded] = useState(false);
  const [testText, setTestText] = useState(() => t("playground.sample"));
  const [testTextEdited, setTestTextEdited] = useState(false);
  const [testResult, setTestResult] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [testError, setTestError] = useState<TestErrorState | null>(null);

  useEffect(() => {
    if (!testTextEdited) {
      setTestText(t("playground.sample"));
    }
  }, [i18n.language, t, testTextEdited]);

  useEffect(() => {
    setApiKey("");
    setCredentialError("");
    setCredentialSaved(false);
    setIsEditingCredential(false);
    setCredentialTestMessage("");
    setCredentialTestSucceeded(false);
  }, [aiConfig.provider]);

  const providers = [
    {
      id: "gemini" as const,
      name: "Google Gemini",
      descriptionKey: "providers.geminiDescription",
      active: geminiActive,
      models: ["gemini-3.5-flash", "gemini-2.5-flash", "gemini-2.5-pro", "gemini-1.5-flash"]
    },
    {
      id: "openrouter" as const,
      name: "OpenRouter",
      descriptionKey: "providers.openrouterDescription",
      active: openrouterActive,
      models: [
        "google/gemini-2.5-flash",
        "meta-llama/llama-3-8b-instruct:free",
        "mistralai/mistral-7b-instruct:free",
        "meta-llama/llama-3-70b-instruct",
        "google/gemini-2.5-pro"
      ]
    }
  ];

  const selectedProvider = providers.find(p => p.id === aiConfig.provider) ?? providers[0];

  const handleProviderSelect = (provider: AIProvider) => {
    const selectedProv = providers.find(p => p.id === provider);
    const defaultModel = selectedProv ? selectedProv.models[0] : "gemini-3.5-flash";
    onUpdateAI({
      provider,
      model: defaultModel
    });
    setTestResult("");
    setTestError(null);
  };

  const handleModelSelect = (modelName: string) => {
    onUpdateAI({
      ...aiConfig,
      model: modelName
    });
  };

  const handleCustomModelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customModel.trim()) {
      onUpdateAI({
        ...aiConfig,
        model: customModel.trim()
      });
    }
  };

  const handleSaveCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim() || readOnly) return;

    setIsSavingCredential(true);
    setCredentialError("");
    setCredentialSaved(false);

    try {
      const savedToken = localStorage.getItem("curator_token");
      const res = await fetch(`/api/ai/credentials/${aiConfig.provider}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(savedToken ? { Authorization: `Bearer ${savedToken}` } : {})
        },
        body: JSON.stringify({ apiKey: apiKey.trim() })
      });

      const data = await safeResponseJson(res);
      if (!res.ok || !data.configured) {
        setCredentialError(data.error || t("credentials.saveError"));
        return;
      }

      setApiKey("");
      setCredentialSaved(true);
      setIsEditingCredential(false);
      setCredentialTestMessage("");
      setCredentialTestSucceeded(false);
      onCredentialStatusChange?.(aiConfig.provider, true);
    } catch (err) {
      console.error(err);
      setCredentialError(t("credentials.saveError"));
    } finally {
      setIsSavingCredential(false);
    }
  };

  const handleTestCredential = async () => {
    if (!apiKey.trim() || readOnly) return;

    setIsTestingCredential(true);
    setCredentialError("");
    setCredentialSaved(false);
    setCredentialTestMessage("");
    setCredentialTestSucceeded(false);

    try {
      const savedToken = localStorage.getItem("curator_token");
      const res = await fetch(`/api/ai/credentials/${aiConfig.provider}/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(savedToken ? { Authorization: `Bearer ${savedToken}` } : {})
        },
        body: JSON.stringify({
          apiKey: apiKey.trim(),
          model: aiConfig.model
        })
      });

      const data = await safeResponseJson(res);
      if (!res.ok || !data.success) {
        setCredentialTestMessage(data.error || t("credentials.testError"));
        return;
      }

      setCredentialTestSucceeded(true);
      setCredentialTestMessage(t("credentials.testSuccess"));
    } catch (err) {
      console.error(err);
      setCredentialTestMessage(t("credentials.testError"));
    } finally {
      setIsTestingCredential(false);
    }
  };

  const handleRemoveCredential = async () => {
    if (readOnly || !selectedProvider.active) return;
    if (!window.confirm(t("credentials.removeConfirm", { provider: selectedProvider.name }))) {
      return;
    }

    setIsRemovingCredential(true);
    setCredentialError("");
    setCredentialSaved(false);
    setCredentialTestMessage("");
    setCredentialTestSucceeded(false);

    try {
      const savedToken = localStorage.getItem("curator_token");
      const res = await fetch(`/api/ai/credentials/${aiConfig.provider}`, {
        method: "DELETE",
        headers: {
          ...(savedToken ? { Authorization: `Bearer ${savedToken}` } : {})
        }
      });

      const data = await safeResponseJson(res);
      if (!res.ok || data.configured !== false) {
        setCredentialError(data.error || t("credentials.removeError"));
        return;
      }

      setApiKey("");
      setIsEditingCredential(false);
      onCredentialStatusChange?.(aiConfig.provider, false);
    } catch (err) {
      console.error(err);
      setCredentialError(t("credentials.removeError"));
    } finally {
      setIsRemovingCredential(false);
    }
  };

  const handleTestAI = async () => {
    if (!testText.trim()) return;
    setIsTesting(true);
    setTestResult("");
    setTestError(null);

    try {
      const savedToken = localStorage.getItem("curator_token");
      const res = await fetch("/api/ai/curate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(savedToken ? { "Authorization": `Bearer ${savedToken}` } : {})
        },
        body: JSON.stringify({
          action: "rephrase",
          text: testText,
          context: "creative and viral"
        })
      });

      const data = await safeResponseJson(res);
      if (res.ok && data.result) {
        setTestResult(data.result);
      } else if (data.error) {
        setTestError({ message: data.error });
      } else {
        setTestError({ messageKey: "errors.generationFallback" });
      }
    } catch (err) {
      console.error(err);
      setTestError({ messageKey: "errors.connectionFallback" });
    } finally {
      setIsTesting(false);
    }
  };

  const isCurrentModelCustom = !selectedProvider.models.includes(aiConfig.model);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5">
      <div className="flex justify-between items-center border-b border-slate-100 pb-4 mb-6">
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-500 animate-pulse" />
            {t("header.title")}
          </h2>
          <p className="text-slate-500 text-xs mt-0.5 font-sans">
            {t("header.description")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
              {t("providers.label")}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {providers.map(p => {
                const isSelected = aiConfig.provider === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => handleProviderSelect(p.id)}
                    disabled={readOnly}
                    aria-label={t("providers.select", { provider: p.name })}
                    className={`text-start p-4 rounded-xl border-2 transition-all cursor-pointer relative flex flex-col justify-between disabled:opacity-80 disabled:cursor-not-allowed ${
                      isSelected
                        ? "border-indigo-600 bg-indigo-50/20 shadow-2xs"
                        : "border-slate-100 hover:border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Cpu className={`w-5 h-5 ${isSelected ? "text-indigo-600" : "text-slate-400"}`} />
                        <span className="font-bold text-sm text-slate-900">{p.name}</span>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed mb-3">
                        {t(p.descriptionKey)}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100/50 w-full">
                      <span className="text-[10px] font-medium text-slate-400">{t("providers.personalCredential")}</span>
                      {p.active ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" /> {t("providers.enabled")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                          <AlertTriangle className="w-3 h-3" /> {t("providers.missingSecret")}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-white p-2 shadow-2xs border border-slate-100">
                <KeyRound className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold text-slate-900">{t("credentials.title")}</h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                  {t("credentials.description", { provider: selectedProvider.name })}
                </p>
              </div>
            </div>

            {selectedProvider.active && !isEditingCredential ? (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
                      <span className="font-mono text-xs font-bold text-slate-800" dir="ltr">
                        •••• •••• ••••
                      </span>
                      <span className="text-xs font-bold text-emerald-700">
                        {t("credentials.configured")}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                      {t("credentials.maskedNote")}
                    </p>
                  </div>

                  {!readOnly ? (
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsEditingCredential(true);
                          setCredentialError("");
                          setCredentialSaved(false);
                          setCredentialTestMessage("");
                          setCredentialTestSucceeded(false);
                        }}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        {t("credentials.replace")}
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveCredential}
                        disabled={isRemovingCredential}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-[11px] font-bold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {isRemovingCredential ? t("credentials.removing") : t("credentials.remove")}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <form onSubmit={handleSaveCredential} className="mt-4">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                  {t("credentials.label", { provider: selectedProvider.name })}
                </label>
                <input
                  type="password"
                  dir="ltr"
                  autoComplete="off"
                  spellCheck={false}
                  value={apiKey}
                  disabled={readOnly || isSavingCredential || isTestingCredential}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setCredentialError("");
                    setCredentialSaved(false);
                    setCredentialTestMessage("");
                    setCredentialTestSucceeded(false);
                  }}
                  placeholder={t("credentials.placeholder", { provider: selectedProvider.name })}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs font-mono outline-hidden focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 disabled:bg-slate-100"
                />

                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handleTestCredential}
                    disabled={readOnly || isSavingCredential || isTestingCredential || apiKey.trim().length < 16}
                    className="rounded-lg border border-indigo-200 bg-white px-4 py-2.5 text-xs font-bold text-indigo-700 transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                  >
                    {isTestingCredential ? t("credentials.testing") : t("credentials.test")}
                  </button>

                  <button
                    type="submit"
                    disabled={readOnly || isSavingCredential || isTestingCredential || apiKey.trim().length < 16}
                    className="rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {isSavingCredential
                      ? t("credentials.saving")
                      : selectedProvider.active
                        ? t("credentials.replace")
                        : t("credentials.save")}
                  </button>

                  {selectedProvider.active && !readOnly ? (
                    <button
                      type="button"
                      onClick={() => {
                        setApiKey("");
                        setIsEditingCredential(false);
                        setCredentialError("");
                        setCredentialTestMessage("");
                        setCredentialTestSucceeded(false);
                      }}
                      className="rounded-lg px-3 py-2.5 text-xs font-bold text-slate-500 transition-colors hover:bg-slate-100"
                    >
                      {t("credentials.cancel")}
                    </button>
                  ) : null}
                </div>
              </form>
            )}

            <div className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
              <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span>{t("credentials.security")}</span>
            </div>

            {credentialSaved ? (
              <p className="mt-3 text-xs font-medium text-emerald-700">
                {t("credentials.saved", { provider: selectedProvider.name })}
              </p>
            ) : null}

            {credentialTestMessage ? (
              <p
                className={`mt-3 text-xs font-medium ${credentialTestSucceeded ? "text-emerald-700" : "text-rose-700"}`}
                dir="auto"
              >
                {credentialTestMessage}
              </p>
            ) : null}

            {credentialError ? (
              <p className="mt-3 text-xs font-medium text-rose-700" dir="auto">
                {credentialError}
              </p>
            ) : null}
          </div>

          <div className="pt-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
              {t("models.label")}
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              {selectedProvider.models.map(m => {
                const isSelected = aiConfig.model === m;
                return (
                  <button
                    key={m}
                    onClick={() => handleModelSelect(m)}
                    disabled={readOnly}
                    aria-label={t("models.select", { model: m })}
                    className={`px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all disabled:opacity-80 disabled:cursor-not-allowed ${
                      isSelected
                        ? "bg-slate-900 text-white shadow-2xs border border-slate-900"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-100"
                    }`}
                  >
                    <span dir="ltr">{m}</span>
                  </button>
                );
              })}

              <button
                onClick={() => handleModelSelect(customModel || "custom-model")}
                disabled={readOnly}
                className={`px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all disabled:opacity-80 disabled:cursor-not-allowed ${
                  isCurrentModelCustom
                    ? "bg-slate-900 text-white shadow-2xs border border-slate-900"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-100"
                }`}
              >
                {t("models.custom")}
              </button>
            </div>

            {!readOnly && (isCurrentModelCustom || aiConfig.provider === "openrouter") && (
              <form onSubmit={handleCustomModelSubmit} className="flex gap-2 max-w-md bg-slate-50 p-1 rounded-lg border border-slate-200">
                <input
                  type="text"
                  dir="ltr"
                  aria-label={t("models.customInputLabel")}
                  placeholder={t("models.customPlaceholder")}
                  value={customModel}
                  onChange={(e) => setCustomModel(e.target.value)}
                  className="flex-1 px-2.5 py-1.5 text-xs outline-hidden font-mono bg-transparent"
                />
                <button
                  type="submit"
                  className="bg-slate-900 text-white text-[11px] font-bold px-3 py-1.5 rounded-md hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  {t("models.apply")}
                </button>
              </form>
            )}

            <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs text-slate-600">
                {t("models.current")} <span className="font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-sm" dir="ltr">{aiConfig.model}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 flex flex-col justify-between">
          <div>
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Play className="w-3.5 h-3.5 text-indigo-500 fill-indigo-500" />
              {t("playground.title")}
            </h3>
            <p className="text-slate-500 text-[11px] leading-relaxed mb-3">
              {t("playground.description")}
            </p>

            {!selectedProvider.active ? (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-800">
                {t("playground.credentialRequired", { provider: selectedProvider.name })}
              </div>
            ) : null}

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                  {t("playground.inputLabel")}
                </label>
                <textarea
                  rows={3}
                  value={testText}
                  dir="auto"
                  onChange={(e) => {
                    setTestText(e.target.value);
                    setTestTextEdited(true);
                  }}
                  placeholder={t("playground.placeholder")}
                  className="w-full p-2 text-xs border border-slate-200 rounded-lg bg-white outline-hidden font-sans resize-none focus:ring-1 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </div>

              {testResult && (
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-2.5">
                  <p className="text-[10px] font-bold text-indigo-700 uppercase mb-1">{t("playground.output")}</p>
                  <p className="text-xs text-slate-700 leading-relaxed font-sans" dir="auto">{testResult}</p>
                </div>
              )}

              {testError && (
                <div className="bg-rose-50 border border-rose-100 rounded-lg p-2.5">
                  <p className="text-[10px] font-bold text-rose-700 uppercase mb-1">{t("playground.error")}</p>
                  <p className="text-xs text-rose-700 leading-relaxed font-sans" dir="auto">{testError.messageKey ? t(testError.messageKey) : testError.message}</p>
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200/50 mt-4">
            <button
              onClick={handleTestAI}
              disabled={isTesting || !testText.trim() || !selectedProvider.active}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-bold text-xs py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-3xs"
            >
              {isTesting ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("playground.testing")}
                </>
              ) : (
                <>
                  <span>{t("playground.run")}</span>
                  <ArrowRight className="rtl-mirror w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
